import { Column, SortingState } from "@tanstack/table-core";
import { ArrowDown, ArrowUp } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const Table = ({ className, ...props }: React.ComponentProps<"table">) => (
  <div className="relative w-full overflow-auto">
    <table
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
);
Table.displayName = "Table";

const TableHeader = ({
  className,
  ...props
}: React.ComponentProps<"thead">) => (
  <thead
    className={cn(
      "[&_tr]:border-b border-slate-6 dark:border-slatedark-6",
      className,
    )}
    {...props}
  />
);
TableHeader.displayName = "TableHeader";

const TableBody = ({ className, ...props }: React.ComponentProps<"tbody">) => (
  <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />
);
TableBody.displayName = "TableBody";

const TableFooter = ({
  className,
  ...props
}: React.ComponentProps<"tfoot">) => (
  <tfoot
    className={cn(
      "border-t bg-slate-subtle font-medium last:[&>tr]:border-b-0",
      className,
    )}
    {...props}
  />
);
TableFooter.displayName = "TableFooter";

const TableRow = ({ className, ...props }: React.ComponentProps<"tr">) => (
  <tr
    className={cn(
      "border-b border-slate-6 dark:border-slatedark-6 transition-colors hover:bg-slatea-subtle data-[state=selected]:bg-slate-subtle",
      className,
    )}
    {...props}
  />
);
TableRow.displayName = "TableRow";

const TableHead = ({ className, ...props }: React.ComponentProps<"th">) => (
  <th
    className={cn(
      "h-12 px-4 text-left align-middle font-medium text-slate-dim [&:has([role=checkbox])]:pr-0",
      className,
    )}
    {...props}
  />
);
TableHead.displayName = "TableHead";

const TableCell = ({ className, ...props }: React.ComponentProps<"td">) => (
  <td
    className={cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className)}
    {...props}
  />
);
TableCell.displayName = "TableCell";

const TableCaption = ({
  className,
  ...props
}: React.ComponentProps<"caption">) => (
  <caption
    className={cn("mt-4 text-sm text-slate-dim", className)}
    {...props}
  />
);
TableCaption.displayName = "TableCaption";

function TableHeaderSortButton<T, Sorting>({
  column,
  children,
  direction = "asc",
  sorting,
  id,
}: {
  column: Column<T>;
  children: string;
  direction?: "asc" | "desc";
  sorting: SortingState;
  id: Sorting;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onPress={() => {
        const currentSort = column.getIsSorted();
        column.toggleSorting(
          currentSort === false
            ? direction === "asc"
            : currentSort === direction,
        );
      }}
      className="-ml-3"
    >
      <div className="flex items-center">
        {children}
        {sorting?.[0]?.id !== id ? (
          <div className="ml-2 h-4 w-4" />
        ) : sorting?.[0]?.desc ? (
          <ArrowDown className="ml-2 h-4 w-4" />
        ) : (
          <ArrowUp className="ml-2 h-4 w-4" />
        )}
      </div>
    </Button>
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableHeaderSortButton,
};
